/**
 * Secure storage facade.
 *
 * On Capacitor native shells, sensitive data (JWT access token, refresh
 * token) lives in the platform's hardware-backed credential store:
 *
 *   • iOS  → Keychain (kSecClassGenericPassword), `WhenUnlockedThisDeviceOnly`
 *     accessibility class. Survives app reinstall? No — we keep it
 *     ephemeral so an attacker who restores from a stolen iCloud backup
 *     to a new device doesn't get our tokens.
 *
 *   • Android → EncryptedSharedPreferences (AES-256 GCM, master key in
 *     AndroidKeyStore). Same threat model.
 *
 * On the web, we fall back to `localStorage` (today's behaviour). Web
 * has no hardware-backed equivalent that's usable from JS, so the
 * threat model there is "browser sandbox + XSS protection in the API"
 * — out of scope for this file.
 *
 * Two surfaces:
 *
 *   • `secureStorage`  — async, platform-aware. Use for new code.
 *   • `secureStorageSync` — best-effort sync read. Used only by the
 *     auth-store hydration path (Zustand `persist` is synchronous so we
 *     keep a mirror in `localStorage` to bootstrap, then upgrade reads
 *     to the secure backend on first async access).
 *
 * Key naming:
 *   We prefix every key with `chai.` so it's obvious in Keychain
 *   inspectors / `adb shell run-as ...` dumps. `chai` = CampusHub AI.
 */
import { isNative } from "./platform";

const KEY_PREFIX = "chai.";

// Lazy plugin handle — only resolved on native to keep web bundle small.
interface SecureStoragePlugin {
  get(opts: { key: string }): Promise<{ value: string } | { value: null }>;
  set(opts: { key: string; value: string }): Promise<void>;
  remove(opts: { key: string }): Promise<void>;
  keys(): Promise<{ keys: string[] }>;
  clear(): Promise<void>;
}

// The plugin handle is carried inside a BOX, never as the resolution value
// of a promise. This is load-bearing, not stylistic.
//
// `registerPlugin()` (which `capacitor-secure-storage-plugin` uses) returns a
// Proxy whose `get` trap manufactures a function for EVERY property name —
// including `then`. If that Proxy is ever the value a promise resolves with
// (`return proxy` from an async fn, or `.then(() => proxy)`), the JS promise
// machinery sees a callable `.then` and treats the Proxy as a thenable: it
// calls `proxy.then(resolve, reject)`, which the Proxy dispatches to the
// native layer as a plugin method literally named `then`. Android/iOS have no
// such method, so it rejects with `"SecureStoragePlugin.then()" is not
// implemented on android` — and, critically, `resolve`/`reject` are NEVER
// invoked. The outer promise then hangs forever: not resolved, not rejected,
// so no `catch`/`try` anywhere up the chain can ever fire.
//
// That is exactly how build 262 shipped a blank screen: `main.tsx` awaited
// `restoreNativeSessionMirror()` before `createRoot()`, that awaited
// `secureStorage.get()`, which awaited this loader — which never settled. On
// any cold start without a localStorage mirror (every fresh install, and any
// device where Android evicted app storage) React never mounted at all.
//
// Boxing the handle in a plain object keeps it out of the thenable-assimilation
// path entirely. Never unwrap the box into a promise value.
interface PluginBox {
  plugin: SecureStoragePlugin;
}

let pluginPromise: Promise<PluginBox> | null = null;

function getPlugin(): Promise<PluginBox> {
  if (!pluginPromise) {
    // The dynamic import keeps the native shim out of the web bundle. Note the
    // object wrapper — see the comment above; returning the plugin directly
    // deadlocks every caller.
    pluginPromise = import("capacitor-secure-storage-plugin").then((m) => ({
      plugin: m.SecureStoragePlugin as unknown as SecureStoragePlugin,
    }));
    // A failed chunk load must not poison the handle for the rest of the
    // session — drop the memo so the next call retries.
    pluginPromise.catch(() => {
      pluginPromise = null;
    });
  }
  return pluginPromise;
}

function k(key: string): string {
  return `${KEY_PREFIX}${key}`;
}

export const secureStorage = {
  async get(key: string): Promise<string | null> {
    if (!isNative()) {
      try {
        return localStorage.getItem(k(key));
      } catch {
        return null;
      }
    }
    try {
      const { plugin } = await getPlugin();
      const res = await plugin.get({ key: k(key) });
      return res?.value ?? null;
    } catch {
      // Native plugin rejects when the key is absent. We treat absence
      // and platform errors identically — both mean "no value".
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (!isNative()) {
      try {
        localStorage.setItem(k(key), value);
      } catch {
        // Private mode / storage-full — swallow. The auth-store will
        // re-issue a tokenless 401 → refresh round-trip and re-store.
      }
      return;
    }
    // Mirror into localStorage FIRST, and never let a secure-store failure
    // skip it. The mirror is what `secureStorageSyncRead` (and therefore the
    // Zustand `persist` bootstrap) reads on cold start, so losing it costs the
    // user their session. Previously the plugin call ran first and unguarded:
    // one rejection threw out of `set()` and the mirror write below never ran.
    try {
      localStorage.setItem(k(key), value);
    } catch {
      // Private mode / storage-full — swallow. The auth-store will
      // re-issue a tokenless 401 → refresh round-trip and re-store.
    }
    try {
      const { plugin } = await getPlugin();
      await plugin.set({ key: k(key), value });
    } catch {
      // Hardware keystore unavailable / plugin missing. The localStorage
      // mirror above still carries the session, so this degrades to web-grade
      // storage rather than signing the user out.
    }
  },

  async remove(key: string): Promise<void> {
    if (!isNative()) {
      try {
        localStorage.removeItem(k(key));
      } catch {
        // ignore
      }
      return;
    }
    try {
      const { plugin } = await getPlugin();
      await plugin.remove({ key: k(key) });
    } catch {
      // Key already absent — fine.
    }
    try {
      localStorage.removeItem(k(key));
    } catch {
      // ignore
    }
  },

  async clear(): Promise<void> {
    if (!isNative()) {
      try {
        // Only clear our own namespaced keys — leave other libraries'
        // localStorage entries (e.g. theme prefs) untouched.
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && key.startsWith(KEY_PREFIX)) toRemove.push(key);
        }
        toRemove.forEach((key) => localStorage.removeItem(key));
      } catch {
        // ignore
      }
      return;
    }
    try {
      const { plugin } = await getPlugin();
      await plugin.clear();
    } catch {
      // ignore
    }
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(KEY_PREFIX)) toRemove.push(key);
      }
      toRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // ignore
    }
  },
};

/**
 * Synchronous read of the `localStorage` mirror.
 *
 * **Do not** use this for security-sensitive checks — it only sees the
 * mirror, not the Keychain/EncryptedSharedPreferences canonical store.
 * It exists exclusively so the auth-store can bootstrap its Zustand
 * `persist` state before the React tree mounts.
 *
 * The first real read of the secure store happens inside an effect on
 * mount, at which point any mismatch is reconciled.
 */
export function secureStorageSyncRead(key: string): string | null {
  try {
    return localStorage.getItem(k(key));
  } catch {
    return null;
  }
}

/**
 * Synchronous write of the `localStorage` mirror only — no secure-store round
 * trip. Used by the cold-start mirror-restore paths, which have already read
 * the canonical value out of the secure store and just need the sync mirror
 * seeded before the first render / first request (CAMP-2026-0327).
 */
export function secureStorageSyncWrite(key: string, value: string): void {
  try {
    localStorage.setItem(k(key), value);
  } catch {
    // Private mode / storage-full — the async secure copy still holds.
  }
}
